import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, HeartHandshake, Copy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Shell from "../components/Shell";
import UpgradeGate from "../components/UpgradeGate";

const RANK = { free: 0, mid: 1, premium: 2 };

export default function Compatibility() {
  const { profile, user } = useAuth();
  const pid = profile?.id;
  const [myToken, setMyToken] = useState(profile?.public_token || null);
  const [tokenA, setTokenA] = useState("");
  const [tokenB, setTokenB] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!pid) return;
    (async () => {
      const { data } = await api.get(`/profiles/${pid}/full`);
      setMyToken(data.public_token);
      if (data.public_token) setTokenA(data.public_token);
    })();
  }, [pid]);

  if (profile === null) return <Navigate to="/setup" replace />;
  if (user && RANK[user.tier || "free"] < RANK.premium)
    return <Shell><UpgradeGate requiredTier="premium" feature="Compatibility" /></Shell>;

  const enableShare = async () => {
    const { data } = await api.post(`/profiles/${pid}/share`);
    setMyToken(data.public_token);
    setTokenA(data.public_token);
    navigator.clipboard?.writeText(data.public_token);
    toast.success("Your token is ready & copied");
  };

  const compare = async () => {
    if (!tokenA || !tokenB) return toast.error("Enter both tokens");
    setLoading(true);
    try {
      const { data } = await api.post(`/compatibility`, { token_a: tokenA, token_b: tokenB });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Comparison failed"); } finally { setLoading(false); }
  };

  return (
    <Shell>
      <p className="overline mb-2">Two blueprints, one dynamic</p>
      <h1 className="font-serif text-5xl mb-8">Compatibility</h1>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="panel p-6">
          <p className="overline mb-3">Your token</p>
          {myToken ? (
            <div className="flex items-center gap-2">
              <code className="text-sm text-primary break-all">{myToken}</code>
              <button onClick={() => { navigator.clipboard?.writeText(myToken); toast.success("Copied"); }} className="text-muted-foreground hover:text-foreground"><Copy size={15} /></button>
            </div>
          ) : (
            <button data-testid="enable-share-btn" onClick={enableShare} className="gold-btn px-5 py-2.5 rounded-full text-sm">Generate my token</button>
          )}
        </div>
        <div className="panel p-6 space-y-3">
          <input data-testid="token-a" value={tokenA} onChange={(e) => setTokenA(e.target.value)} placeholder="Blueprint token A"
            className="w-full px-4 py-2.5 rounded-xl border border-border focus:border-primary outline-none text-sm" />
          <input data-testid="token-b" value={tokenB} onChange={(e) => setTokenB(e.target.value)} placeholder="Blueprint token B"
            className="w-full px-4 py-2.5 rounded-xl border border-border focus:border-primary outline-none text-sm" />
          <button data-testid="compare-btn" onClick={compare} disabled={loading} className="gold-btn w-full py-2.5 rounded-full font-medium flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <HeartHandshake size={16} />} Compare
          </button>
        </div>
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="panel p-8" data-testid="compat-result">
          <div className="flex items-center justify-center gap-8 mb-8">
            <div className="text-center">
              <p className="font-serif text-xl text-primary">{result.a.hd_type}</p>
              <p className="text-xs text-muted-foreground">{result.a.sun} · {result.a.mbti} · {result.a.enneagram}</p>
            </div>
            <div className="text-center">
              <div className="font-serif text-5xl text-primary" data-testid="overlap-score">{result.overlap_score}</div>
              <p className="overline">overlap</p>
            </div>
            <div className="text-center">
              <p className="font-serif text-xl text-primary">{result.b.hd_type}</p>
              <p className="text-xs text-muted-foreground">{result.b.sun} · {result.b.mbti} · {result.b.enneagram}</p>
            </div>
          </div>
          <p className="text-foreground/90 leading-relaxed text-center max-w-2xl mx-auto">{result.narrative}</p>
        </motion.div>
      )}
    </Shell>
  );
}
